<script lang="ts" setup>
  import type { Entry } from 'contentful'
  import {
    isTypeAccordionComponent,
    isTypeCardComponent,
    isTypeCardGridComponent,
    isTypeDividerComponent,
    isTypeGiftCardBalanceCheckerComponent,
    isTypeGroupBookingChopeComponent,
    isTypeGroupBookingObeeComponent,
    isTypeTypeformComponent,
    isTypeHeroComponent,
    isTypePanelHeroComponent,
    isTypeImageComponent,
    isTypeImageTextComponent,
    isTypePromotionComponent,
    isTypeSpacerComponent,
    isTypeTextComponent,
    isTypeYouTubeComponent,
    type TypeAccordionComponent,
    type TypeCardComponent,
    type TypeCardGridComponent,
    type TypeDividerComponent,
    type TypeGiftCardBalanceCheckerComponent,
    type TypeGroupBookingChopeComponent,
    type TypeGroupBookingObeeComponent,
    type TypeTypeformComponent,
    type TypeHeroComponent,
    type TypePanelHeroComponent,
    type TypeImageComponent,
    type TypeImageTextComponent,
    type TypePageSkeleton,
    type TypePromotionComponent,
    type TypeSpacerComponent,
    type TypeTextComponent,
    type TypeYouTubeComponent
  } from '@/types/contentful'
  import { definedItems } from '@/utils/contentful'
  import type { ImgLoading } from '@/types/media'
  import { SITE_ENVIRONMENTS } from '~/constants/siteEnvironment'

  const props = withDefaults(
    defineProps<{
      pageData: Entry<TypePageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
      eagerLoadModuleAmount?: number
      preloadModuleAmount?: number
    }>(),
    {
      eagerLoadModuleAmount: 4,
      preloadModuleAmount: 2
    }
  )

  const {
    public: { siteEnvironment }
  } = useRuntimeConfig()

  const { relishCheck } = useConditionalDisplay()
  const isClient = ref(false)

  onMounted(() => {
    isClient.value = true
  })
  /**
   * @description Determines if a module should be eagerly loaded or lazily loaded to improve FCP.
   * As the app is SSR, we cannot determine which modules are above the fold,
   * thus we want manually define force modules, which contain images, to eagerly load.
   * @param index {number} - The index of the module in the array.
   */
  function shouldEagerLoadModule(index: number): ImgLoading {
    return index + 1 <= props.eagerLoadModuleAmount ? 'eager' : 'lazy'
  }

  /**
   * @description Determines if a module should be preloaded to improve LCP.
   * @param index {number} - The index of the module in the array.
   */
  function shouldPreloadModule(index: number): boolean {
    return index + 1 <= props.preloadModuleAmount
  }
</script>

<template>
  <template v-for="(component, index) in definedItems(pageData.fields.components)" :key="component.sys.id">
    <div
      :id="component.fields.anchorTag"
      :class="{
        'is-visible':
          component.fields.base?.fields?.visibility === 'Everyone' ||
          (isClient && relishCheck(component.fields.base?.fields?.visibility)),
        'is-hidden': !(
          component.fields.base?.fields?.visibility === 'Everyone' ||
          (isClient && relishCheck(component.fields.base?.fields?.visibility))
        )
      }"
    >
      <MAccordionDrawer
        v-if="isTypeAccordionComponent(component)"
        :data="component as TypeAccordionComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
      />

      <MCardCarousel
        v-if="isTypeCardComponent(component)"
        :data="component as TypeCardComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
        :loading="shouldEagerLoadModule(index)"
        :preload="shouldPreloadModule(index)"
      />

      <MCardGrid
        v-if="isTypeCardGridComponent(component)"
        :data="component as TypeCardGridComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
        :loading="shouldEagerLoadModule(index)"
        :preload="shouldPreloadModule(index)"
      />

      <MDivider
        v-if="isTypeDividerComponent(component)"
        :data="component as TypeDividerComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
      />

      <MTextAndButtons
        v-if="isTypeTextComponent(component)"
        :data="component as TypeTextComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
      />

      <MImage
        v-if="isTypeImageComponent(component)"
        :data="component as TypeImageComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
        :loading="shouldEagerLoadModule(index)"
        :preload="shouldPreloadModule(index)"
      />

      <MImageAndText
        v-if="isTypeImageTextComponent(component)"
        :data="component as TypeImageTextComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
        :loading="shouldEagerLoadModule(index)"
        :preload="shouldPreloadModule(index)"
      />

      <MHero
        v-if="isTypeHeroComponent(component)"
        :data="component as TypeHeroComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
        :loading="shouldEagerLoadModule(index)"
        :preload="shouldPreloadModule(index)"
      />

      <MPanelHero
        v-if="isTypePanelHeroComponent(component)"
        :data="component as TypePanelHeroComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
        :loading="shouldEagerLoadModule(index)"
        :preload="shouldPreloadModule(index)"
      />

      <MSpacer
        v-if="isTypeSpacerComponent(component)"
        :data="component as TypeSpacerComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
      />

      <MYouTube
        v-if="isTypeYouTubeComponent(component)"
        :data="component as TypeYouTubeComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
      />

      <MPromotions
        v-if="isTypePromotionComponent(component)"
        :data="component as TypePromotionComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
        :loading="shouldEagerLoadModule(index)"
        :preload="shouldPreloadModule(index)"
      />

      <MGroupBookingObee
        v-if="isTypeGroupBookingObeeComponent(component) && siteEnvironment === SITE_ENVIRONMENTS.AUSTRALIA"
        :data="component as TypeGroupBookingObeeComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
      />

      <MGroupBookingChope
        v-if="isTypeGroupBookingChopeComponent(component) && siteEnvironment === SITE_ENVIRONMENTS.INDONESIA"
        :data="component as TypeGroupBookingChopeComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
      />

      <MTypeform
        v-if="isTypeTypeformComponent(component)"
        :data="component as TypeTypeformComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
      />

      <MGiftCardBalanceChecker
        v-if="isTypeGiftCardBalanceCheckerComponent(component)"
        :data="component as TypeGiftCardBalanceCheckerComponent<'WITHOUT_UNRESOLVABLE_LINKS', string>"
      />
    </div>
  </template>
</template>
<style scoped>
  /* Hide components initially */
  .is-hidden {
    display: none;
  }

  /* Show components when they are visible */
  .is-visible {
    display: block;
  }
</style>
